import { Injectable, inject } from '@angular/core';
import { GalleryDto, UpdateGalleryBodyDto } from '@nusign/picflow-backend-types';
import { forkJoin, from, map, of, switchMap, take, tap, throwError } from 'rxjs';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { GalleryMemberService } from './gallery-member.service';
import { GalleryService } from './gallery.service';
import { SectionService } from './section.service';
import { UserGroupService } from './user-group.service';
import { ViewGalleryHierarchyService } from './view-gallery-hierarchy.service';

@Injectable({
  providedIn: 'root',
})
export class MoveGalleryService {
  private sectionService = inject(SectionService);
  private galleryService = inject(GalleryService);
  private galleryMemberService = inject(GalleryMemberService);
  private userGroupService = inject(UserGroupService);
  private hierarchyService = inject(ViewGalleryHierarchyService);

  moveGallery(gallery: GalleryDto, moveToGallery?: GalleryDto, moveToFolderId?: string | null) {
    if (moveToGallery) {
      return this.moveGalleryToGallery(gallery, moveToGallery);
    }
    return this.moveGalleryToFolder(gallery, moveToFolderId);
  }

  moveGalleryToGallery(gallery: GalleryDto, moveTo: GalleryDto) {
    // Moving a gallery into another gallery
    const galleryId = gallery.id;
    const updateParams: UpdateGalleryBodyDto = {};
    return this.sectionService.getSections(galleryId).pipe(
      take(1),
      map(sections => sections.filter(s => s.object === 'gallery')),
      switchMap(subGallerySections => {
        if (!subGallerySections.length) return of(undefined);
        return this.galleryService.getSubGalleries(galleryId).pipe(
          take(1),
          switchMap(subGalleries => {
            if (subGalleries.length) {
              return throwError(() => 'cant_dupe_gallery_with_subgalleries');
            }
            // Deleting empty sub gallery sections
            return forkJoinSafe(subGallerySections.map(section => this.sectionService.deleteSection(section)));
          })
        );
      }),
      switchMap(() => this.sectionService.getOrCreateSection('gallery', moveTo.id)),
      switchMap(section => {
        updateParams.section = section.id;
        updateParams.parent_gallery = moveTo.id;
        updateParams.public_access = null;
        updateParams.tenant_access = null;
        updateParams.password_enabled = moveTo.password_enabled;
        updateParams.password = moveTo.password || undefined;
        updateParams.show_in_profile = false;
        updateParams.requires_user_identification = moveTo.requires_user_identification;
        updateParams.folder = null;

        this.updateHierarchy(gallery, moveTo);

        // delete any gallery members, or gallery groups
        return forkJoin([
          from(this.galleryService.updateGallery(gallery, updateParams)),
          this.galleryMemberService.removeAll(galleryId),
          this.userGroupService.removeAll(galleryId),
        ]).pipe(map(([gallery]) => gallery));
      })
    );
  }

  private updateHierarchy(gallery: GalleryDto, moveTo: GalleryDto) {
    const parentHierarchy = this.hierarchyService.hierarchies.find(h => h.galleryId === gallery.parent_gallery);
    if (parentHierarchy) {
      parentHierarchy.childrenIds = parentHierarchy.childrenIds.filter(id => id !== gallery.id);
      parentHierarchy.childrenPaths = parentHierarchy.childrenPaths.filter(path => path !== gallery.path);
    }

    this.hierarchyService.add(moveTo.id, [gallery.id], [gallery.path]);
  }

  moveGalleryToFolder(gallery: GalleryDto, moveTo: string | null) {
    const updateParams: UpdateGalleryBodyDto = {};
    updateParams.folder = moveTo || null;

    return (
      gallery.section
        ? this.galleryService.getGallery(gallery.parent_gallery as string).pipe(
            take(1),
            tap(parentGallery => {
              updateParams.section = null;
              updateParams.parent_gallery = null;
              if (!gallery.public_access) updateParams.public_access = parentGallery.public_access as never;
              if (!gallery.tenant_access) updateParams.tenant_access = parentGallery.tenant_access as never;
            })
          )
        : of(undefined)
    ).pipe(
      switchMap(() => {
        // Moving a gallery to a folder
        gallery.parent_gallery = undefined;
        return from(this.galleryService.updateGallery(gallery, updateParams));
      })
    );
  }
}
